import { useNavigate } from 'react-router-dom';
import { useState, useRef } from 'react';

export default function BusinessPage() {
  const navigate = useNavigate();
  const contractRef = useRef<HTMLDivElement>(null);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [clientName, setClientName] = useState('');
  const [billingPlan, setBillingPlan] = useState<'monthly' | 'yearly'>('monthly');

  const handleContractScroll = () => {
    if (contractRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = contractRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 10) {
        setHasScrolledToBottom(true);
      }
    }
  };

  const contractSections = [
    {
      number: '01',
      title: 'Acceptance of Terms',
      body: 'By submitting this form, you ("Client") acknowledge that you have read, understood, and agree to be bound by these Terms & Conditions set forth by Yasmine Creates ("Company"). Submission of this form constitutes acceptance of all terms outlined herein.',
    },
    {
      number: '02',
      title: 'Hosting Requirement',
      body: 'All websites designed and delivered by Yasmine Creates require an active hosting plan to remain published, live, and accessible on the internet. Hosting plans are available on a monthly basis at $59.99/month or an annual basis at $499.99/year. Failure to maintain an active hosting plan will result in the website being taken offline and rendered inaccessible to all visitors and customers. Yasmine Creates does not build or deliver websites without an accompanying hosting agreement. Clients who are unwilling or unable to commit to a hosting plan are ineligible for services.',
    },
    {
      number: '03',
      title: 'Scope of Services',
      body: 'The Business service is built for established businesses needing reservations, booking, or appointment scheduling integrations. Perfect for restaurants, salons, beauty professionals, and service-based businesses. All websites are designed, built, and hosted solely by Yasmine Creates. The Company reserves the right to decline any project deemed outside the scope of deliverable services and will communicate this to the Client prior to engagement.',
    },
    {
      number: '04',
      title: 'Confidentiality of Proprietary Process',
      body: "The tools, software, platforms, and methodologies utilized by Yasmine Creates in the design and development of websites are proprietary and strictly confidential. The Company is under no obligation to disclose, discuss, or share any aspect of its build process with the Client at any time. Inquiries regarding the Company's tools or processes will be grounds for immediate termination of the project without refund of any payments made.",
    },
    {
      number: '05',
      title: 'Hosting Activation',
      body: "Hosting does not activate until the Client's website has been fully completed, approved, and officially marked as ready to go live by Yasmine Creates. Upon the website being marked live, the Client's selected hosting plan will activate and recurring billing will commence accordingly.",
    },
    {
      number: '06',
      title: 'No Inactive Websites',
      body: 'Yasmine Creates does not design or deliver websites that will not be actively hosted and maintained. The Company will not build a website that is not intended to go live. Any Client who does not intend to maintain an active hosting plan upon completion is ineligible for services.',
    },
    {
      number: '07',
      title: 'Build Fee & Payment Terms',
      body: 'The one-time build fee of $450.00 is due in full before project commencement. This fee is non-refundable once work has begun. Monthly hosting at $59.99/month begins the month following site launch. Alternatively, clients may opt for annual hosting at $499.99/year, which must be paid in full along with the build fee ($949.99 total) before project commencement.',
    },
    {
      number: '08',
      title: 'Client Materials & Timeline',
      body: 'The Client must provide all required materials (logo files, brand guidelines, written copy, images, domain credentials, and third-party platform credentials if applicable) before work can begin. The website will be delivered within 1 week of receiving complete materials. Delays in material provision will extend the timeline accordingly. Unlimited revisions are included until the Client approves the site for launch.',
    },
    {
      number: '09',
      title: 'Post-Launch Edit Requests',
      body: 'Once the Client has approved the website and it has been marked live, any subsequent edit requests are billed at $32.00 per request. Each request covers up to 12 individual edits. Additional edits beyond 12 per request will require a new request and additional payment.',
    },
    {
      number: '10',
      title: 'Late Payment & Site Suspension',
      body: 'If monthly hosting payments are not received by the due date, the website will be taken offline immediately and will remain inaccessible until payment is received in full. The Company is not responsible for any loss of business, traffic, or revenue resulting from site suspension due to non-payment.',
    },
  ];

  const isButtonEnabled = hasScrolledToBottom && clientName.trim().length > 0;

  const handleProceedToPayment = () => {
    const stripeLinks = {
      monthly: 'https://buy.stripe.com/eVqeV6gEd0gaeLoeBffEk01',
      yearly: 'https://buy.stripe.com/cNibIU4Vv3sm46KdxbfEk08',
    };

    window.open(stripeLinks[billingPlan], '_blank');
  };

  const getTotalToday = () => {
    if (billingPlan === 'monthly') {
      return '$450.00';
    }
    return '$949.99';
  };

  return (
    <div className="min-h-screen bg-white font-body flex flex-col">
      {/* Header */}
      <header className="bg-[#1a1a1a] relative overflow-hidden">
        <div className="absolute top-0 left-0 w-1 h-full bg-[#F5C518]"></div>
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        ></div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 sm:px-10 py-12 sm:py-16">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-white/40 hover:text-[#F5C518] text-xs tracking-widest uppercase mb-10 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line text-sm"></i>
            Back to Home
          </button>

          <p className="text-[#F5C518] text-xs tracking-[0.25em] uppercase font-medium mb-3">
            Project Agreement
          </p>
          <h1 className="font-display text-white text-4xl sm:text-5xl tracking-wide leading-tight mb-3">
            BUSINESS
          </h1>
          <p className="text-white/40 text-sm">Let's make it official</p>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 max-w-6xl mx-auto w-full px-6 sm:px-10 py-14">

        {/* Project Overview — compact inline rows */}
        <div className="mb-14 space-y-px border border-[#eee] rounded-sm overflow-hidden">

          {/* Row 1 — What You're Getting */}
          <div className="flex items-start gap-5 bg-[#FAFAF8] px-6 py-5 hover:bg-[#F5C518]/5 transition-colors">
            <div className="w-8 h-8 flex items-center justify-center shrink-0 mt-0.5">
              <i className="ri-checkbox-circle-line text-lg text-[#F5C518]"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-display text-[#1a1a1a] text-sm tracking-widest uppercase mb-1.5">What You're Getting</p>
              <p className="text-xs text-[#666] leading-relaxed mb-2.5">
                The Business service is built for established businesses needing reservations, booking, or appointment scheduling integrations. Perfect for restaurants, salons, beauty professionals, and service-based businesses.
              </p>
            </div>
          </div>

          {/* Row 2 — What It Costs */}
          <div className="flex items-start gap-5 bg-white px-6 py-5 hover:bg-[#F5C518]/5 transition-colors">
            <div className="w-8 h-8 flex items-center justify-center shrink-0 mt-0.5">
              <i className="ri-money-dollar-circle-line text-lg text-[#F5C518]"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-display text-[#1a1a1a] text-sm tracking-widest uppercase mb-2">What It Costs</p>
              <div className="flex flex-wrap gap-x-6 gap-y-1">
                <span className="text-xs text-[#666] flex items-center gap-1.5">
                  <span className="w-1 h-1 rounded-full bg-[#F5C518] inline-block shrink-0"></span>
                  <strong className="text-[#1a1a1a]">$450.00</strong>&nbsp;one-time build fee
                </span>
                <span className="text-xs text-[#666] flex items-center gap-1.5">
                  <span className="w-1 h-1 rounded-full bg-[#F5C518] inline-block shrink-0"></span>
                  <strong className="text-[#1a1a1a]">$59.99/mo</strong>&nbsp;hosting after launch
                </span>
                <span className="text-xs text-[#666] flex items-center gap-1.5">
                  <span className="w-1 h-1 rounded-full bg-[#F5C518] inline-block shrink-0"></span>
                  <strong className="text-[#1a1a1a]">$949.99</strong>&nbsp;build + yearly hosting today
                </span>
              </div>
            </div>
          </div>

          {/* Row 3 — What I Need */}
          <div className="flex items-start gap-5 bg-[#FAFAF8] px-6 py-5 hover:bg-[#F5C518]/5 transition-colors">
            <div className="w-8 h-8 flex items-center justify-center shrink-0 mt-0.5">
              <i className="ri-file-list-3-line text-lg text-[#F5C518]"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-display text-[#1a1a1a] text-sm tracking-widest uppercase mb-2">What I Need From You</p>
              <div className="flex flex-wrap gap-x-5 gap-y-1">
                {['Logo files', 'Brand colors & fonts', 'All written copy', 'Photos & images', 'Domain credentials', 'Booking / Shopify credentials (if applicable)'].map((item) => (
                  <span key={item} className="text-xs text-[#666] flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-[#F5C518] inline-block shrink-0"></span>
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Row 4 — Timeline + Post-Launch + Late Payments in one row */}
          <div className="flex items-start gap-5 bg-white px-6 py-5 hover:bg-[#F5C518]/5 transition-colors">
            <div className="w-8 h-8 flex items-center justify-center shrink-0 mt-0.5">
              <i className="ri-time-line text-lg text-[#F5C518]"></i>
            </div>
            <div className="flex-1 min-w-0 flex flex-wrap gap-x-10 gap-y-3">
              <div>
                <p className="font-display text-[#1a1a1a] text-sm tracking-widest uppercase mb-1">Timeline</p>
                <p className="text-xs text-[#666]">Ready within <strong className="text-[#1a1a1a]">1 week</strong> of receiving content</p>
              </div>
              <div>
                <p className="font-display text-[#1a1a1a] text-sm tracking-widest uppercase mb-1">Post-Launch Edits</p>
                <p className="text-xs text-[#666]"><strong className="text-[#1a1a1a]">$32.00/request</strong> · covers up to 12 edits</p>
              </div>
              <div>
                <p className="font-display text-[#1a1a1a] text-sm tracking-widest uppercase mb-1">Late Payments</p>
                <p className="text-xs text-[#666]">Site goes <strong className="text-[#1a1a1a]">offline</strong> until payment is received</p>
              </div>
            </div>
          </div>

        </div>

        {/* Contract Section */}
        <div className="border-t border-[#eee] pt-14">
          <div className="mb-8">
            <h2 className="font-display text-[#1a1a1a] text-3xl tracking-wide mb-2">
              AGREEMENT
            </h2>
            <p className="text-[#666] text-sm">
              Please read the complete agreement below before proceeding
            </p>
          </div>

          {/* Scrollable Contract Box */}
          <div className="relative bg-[#FAFAF8] border border-[#eee] rounded-sm overflow-hidden">
            {/* Scroll Indicator */}
            {!hasScrolledToBottom && (
              <div className="absolute top-0 left-0 right-0 bg-[#F5C518] text-[#1a1a1a] text-xs font-medium py-2 px-6 flex items-center justify-center gap-2 z-10">
                <span>Scroll to continue</span>
                <i className="ri-arrow-down-line"></i>
              </div>
            )}

            {/* Contract Content */}
            <div
              ref={contractRef}
              onScroll={handleContractScroll}
              className="h-[500px] overflow-y-scroll px-8 py-10 space-y-8"
            >
              {contractSections.map((section) => (
                <div key={section.number} className="flex gap-6 group">
                  <div className="pt-0.5 shrink-0">
                    <span className="font-display text-[#F5C518] text-base tracking-widest">
                      {section.number}
                    </span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-[#1a1a1a] font-semibold text-base mb-2 tracking-wide">
                      {section.title}
                    </h3>
                    <p className="text-[#666] text-sm leading-relaxed">{section.body}</p>
                  </div>
                </div>
              ))}

              {/* Last Updated */}
              <div className="pt-6 border-t border-[#eee]">
                <p className="text-xs text-[#bbb] tracking-wide">
                  Last updated:{' '}
                  {new Date().toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </p>
              </div>
            </div>

            {/* Bottom Fade */}
            {!hasScrolledToBottom && (
              <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-[#FAFAF8] to-transparent pointer-events-none"></div>
            )}
          </div>

          {/* Signature Section */}
          <div className="mt-10 bg-white border border-[#eee] p-8 rounded-sm">
            <h3 className="font-display text-[#1a1a1a] text-xl tracking-wide mb-6">
              SIGNATURE
            </h3>
            <div className="space-y-4">
              <div>
                <label htmlFor="clientName" className="block text-sm text-[#666] mb-2">
                  Full Name <span className="text-[#F5C518]">*</span>
                </label>
                <input
                  type="text"
                  id="clientName"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  placeholder="Enter your full name"
                  className="w-full px-4 py-3 border border-[#ddd] rounded-sm text-sm focus:outline-none focus:border-[#F5C518] transition-colors"
                  disabled={!hasScrolledToBottom}
                />
              </div>
              <div>
                <label className="block text-sm text-[#666] mb-2">Date</label>
                <input
                  type="text"
                  value={new Date().toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                  disabled
                  className="w-full px-4 py-3 border border-[#ddd] rounded-sm text-sm bg-[#f9f9f9] text-[#999]"
                />
              </div>
            </div>
          </div>

          {/* Billing Plan Selection */}
          <div className="mt-10 bg-white border border-[#eee] p-8 rounded-sm">
            <h3 className="font-display text-[#1a1a1a] text-xl tracking-wide mb-6">
              SELECT YOUR PLAN
            </h3>

            {/* Toggle */}
            <div className="flex items-center justify-center gap-0 bg-[#FAFAF8] p-1 rounded-sm mb-8 max-w-md mx-auto">
              <button
                onClick={() => setBillingPlan('monthly')}
                className={`flex-1 py-3 px-6 rounded-sm text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  billingPlan === 'monthly'
                    ? 'bg-[#F5C518] text-[#1a1a1a]'
                    : 'bg-transparent text-[#666] hover:text-[#1a1a1a]'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingPlan('yearly')}
                className={`flex-1 py-3 px-6 rounded-sm text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  billingPlan === 'yearly'
                    ? 'bg-[#F5C518] text-[#1a1a1a]'
                    : 'bg-transparent text-[#666] hover:text-[#1a1a1a]'
                }`}
              >
                Yearly
              </button>
            </div>

            {/* Plan Summary */}
            <div className="bg-[#FAFAF8] border border-[#eee] p-6 rounded-sm space-y-4">
              <div className="flex items-center justify-between pb-4 border-b border-[#eee]">
                <span className="text-sm text-[#666]">Build Fee</span>
                <span className="text-sm font-medium text-[#1a1a1a]">$450.00</span>
              </div>

              {billingPlan === 'monthly' ? (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[#666]">Hosting (starts next month)</span>
                    <span className="text-sm font-medium text-[#1a1a1a]">$59.99/mo</span>
                  </div>
                  <div className="pt-4 border-t border-[#eee] flex items-center justify-between">
                    <span className="font-display text-base tracking-wide text-[#1a1a1a]">
                      DUE TODAY
                    </span>
                    <span className="font-display text-2xl tracking-wide text-[#F5C518]">
                      $450.00
                    </span>
                  </div>
                  <p className="text-xs text-[#999] pt-2">
                    Monthly hosting billing begins the month after your site goes live
                  </p>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[#666]">Yearly Hosting (12 months)</span>
                    <span className="text-sm font-medium text-[#1a1a1a]">$499.99</span>
                  </div>
                  <div className="pt-4 border-t border-[#eee] flex items-center justify-between">
                    <span className="font-display text-base tracking-wide text-[#1a1a1a]">
                      DUE TODAY
                    </span>
                    <span className="font-display text-2xl tracking-wide text-[#F5C518]">
                      $949.99
                    </span>
                  </div>
                  <p className="text-xs text-[#999] pt-2">
                    Build fee + full year of hosting paid upfront
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <button
            onClick={handleProceedToPayment}
            disabled={!isButtonEnabled}
            className={`mt-8 w-full py-4 rounded-sm font-medium text-sm tracking-wide transition-all whitespace-nowrap ${
              isButtonEnabled
                ? 'bg-[#F5C518] text-[#1a1a1a] hover:bg-[#d4a814] cursor-pointer'
                : 'bg-[#eee] text-[#bbb] cursor-not-allowed'
            }`}
            title={
              !isButtonEnabled ? 'Please read and sign the agreement above' : ''
            }
          >
            {isButtonEnabled
              ? `Sign & Proceed to Payment — ${getTotalToday()}`
              : 'Please Read & Sign Above'}
          </button>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#FAFAF8] border-t border-[#eee]">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="font-display text-sm tracking-[0.1em] text-[#1a1a1a]">
            YASMINE CREATES
          </span>
          <p className="text-xs text-[#999]">&copy; 2025 Yasmine Creates. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}